import Http from '@helpers/Http';
import React, { useEffect, useRef } from 'react';
import './index.module.less';
import { Api } from '@apis/client';
import NavBar from '@components/NavBar';
import { getDateAndTime } from '@helpers/DateTime/timeUtils';
import Filter from '@helpers/Filter';
import { TNotification } from '@apis/notification/getNotificationList';
import userStore from '@stores/user';
import NotificationItem from './notificationItem';
import notificationStore from '@stores/notification';
import { observer } from 'mobx-react';
import PullRefresher from '@components/PullRefresher';
import EmptyView from '@views/sections/EmptyView';

const oneGroupRange = 5 * 60 * 1000;

const NotificationList = () => {

  const params = useRef(Filter.urlParams(window.location.search));
  const groupName = decodeURIComponent(params.current.groupName);
  const pullRefresherRef = useRef<PullRefresher>(null);
  const groupId = params.current.groupUid;

  const fetchNotificationList = async (queryTime?: number, notificationId?: number, loadMore?: boolean) => {
    const result = await Http.post(Api.GetNotificationList, {
      userUid: userStore.userInfo.uid || '',
      groupUid: groupId,
      queryTime: queryTime,
      pageSize: notificationStore.loadPageSize,
      id: notificationId
    }).finally(() => {
      if (loadMore) {
        pullRefresherRef.current?.reset();
      }
    });
    // 处理 Notification 的 Json 字符串转换
    if (result?.data) {
      result.data.forEach((item: TNotification) => {
        item.bodyInfo = item.body ? JSON.parse(item.body) : null
        item.photoInfo = item.photo ? JSON.parse(item.photo) : null
        item.actionInfo = item.body ? JSON.parse(item.action) : null
      });
    }
    if (loadMore) {
      notificationStore.appendNotificationList(result?.data || []);
    } else {
      notificationStore.setNotificationList(result?.data || []);
    }
    return (() => {
      notificationStore.setNotificationList(null);
    });
  }

  useEffect(() => {

    const markGroupHasRead = async () => {
      await Http.post(Api.PostSetNotificatioNGroupHasRead, {
        userUid: userStore.userInfo.uid || '',
        groupUid: groupId
      });
    }

    markGroupHasRead();
    fetchNotificationList(Date.now(), null);
    return(() => {
      notificationStore.setNotificationList(null);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let notificationItemList: JSX.Element[] = [];

  const pushTimeDividerGroup = (itemInfo?: TNotification) => {
    if (!itemInfo) {
      return;
    }
    itemInfo.groupTime = itemInfo.createTime;
    const groupTimeText = getDateAndTime(itemInfo.groupTime);
    notificationItemList.push(<div key={`divider-${itemInfo.id}`} styleName='time-divider'>{groupTimeText}</div>);
    notificationItemList.push(<NotificationItem key={`item-${itemInfo.id}`} itemInfo={itemInfo} onDeleteItem={handleDeleteNotificationItem} />);
  };

  const pushNormalDividerGroup = (itemInfo?: TNotification) => {
    if (!itemInfo) {
      return;
    }
    notificationItemList.push(<div key={`divider-${itemInfo.id}`} styleName='divider'>{ }</div>);
    notificationItemList.push(<NotificationItem key={`item-${itemInfo.id}`} itemInfo={itemInfo} onDeleteItem={handleDeleteNotificationItem} />);
  }

  const handleDeleteNotificationItem = async (item: TNotification) => {
    await Http.post(Api.DeleteNotification, {
      ids: [item.id]
    }).then(() => {
      notificationStore.deleteNotification(item);
    });
  }

  const notificationList = notificationStore.notificationList;
  if (notificationList) {
    for (let index = 0; index < notificationList.length; index++) {
      const lastItemIndex = index - 1 < 0 ? -1 : index - 1;
      const currentItem = notificationList[index];

      // 针对首个 Item 或 size == 1 的情况
      if (lastItemIndex === -1) {
        pushTimeDividerGroup(currentItem);
        continue;
      }

      const lastItem = notificationList[lastItemIndex];
      const lastItemGroupTime = lastItem.groupTime;
      const currentItemCreatTime = currentItem.createTime;
      if (lastItemGroupTime) {
        // 有分组参考，分两种情况
        // 1. currentItem 存在创建时间，是否满足分组条件？
        // 2. currentItem 没有创建时间，不具备可比性，无法独自成组（基本不可能）
        if (currentItemCreatTime) {
          const differTime = lastItemGroupTime - currentItemCreatTime;
          if (differTime >= oneGroupRange) { // 满足自创分组
            pushTimeDividerGroup(currentItem);
            continue;
          }
        }
        currentItem.groupTime = lastItemGroupTime;
        pushNormalDividerGroup(currentItem);
      } else {
        // 没有分组参考，尝试自创分组，并记录分组时间
        if (currentItemCreatTime) {
          pushTimeDividerGroup(currentItem);
        } else {
          pushNormalDividerGroup(currentItem);
        }
      }
    }
  }

const hasData = notificationStore.notificationList && notificationStore.notificationList.length > 0;
const navTitle = (!groupName || groupName === 'undefined') ? '通知列表' : groupName;

return (
  <div styleName={'container'} style={{ height: document.body.clientHeight }}>
    <NavBar title={navTitle} />
    {
      hasData &&
      <div styleName='list-container'>
        {notificationItemList}
        <div styleName='space'/>
        <PullRefresher
          onRefresh={() => {
            const lastIndex = notificationStore.notificationList.length - 1;
            const lastNotificationId = notificationStore.notificationList[lastIndex].id;
            fetchNotificationList(null, lastNotificationId, true);
          }}
          loadingText={notificationStore.noMoreData ? '已经滑到最底部啦' : '加载中...'}
          ref={pullRefresherRef}
        />
      </div>
    }
    {notificationStore.notificationList?.length === 0 && <EmptyView />}
  </div>);
}

export default observer(NotificationList);